import React from 'react';
import PropTypes from 'prop-types';
import { Statistics, Numbers, TooltipHelp } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import Style from './cardsStats.module.css';

import useAppContext from '../../context/useAppContext';
import i18n from 'i18next';
import { LabelStat } from './channelsStats';

function SubtitleStatWithTooltip({ name = '', countDeleted = 0, countReservation = 0, sumRevenueDeleted = 0, sumRevenueReservation = 0 }) {
    const locale = i18n.language;
    const { t } = useTranslation();
    const [{ event }] = useAppContext();

    const displayTooltip = React.useMemo(() => {
        if (['channels', 'rates'].includes(name)) {
            return false;
        }

        if (name === 'attendees' && countDeleted === 0 && countReservation === 0) {
            return false;
        }

        if (name === 'revenue' && sumRevenueDeleted === 0 && sumRevenueReservation === 0) {
            return false;
        }

        return true;
    }, [countDeleted, countReservation, sumRevenueReservation, sumRevenueDeleted, name]);

    return (
        <span>
            {t(`ticket.attendees_stats.statsCards.${name}`)}
            {displayTooltip && (
                <TooltipHelp
                    position="bottom"
                    triggerOnHover
                    title={
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {name === 'attendees' && countReservation > 0 && (
                                <LabelStat
                                    label={t('ticket.attendees_stats.channels.labelCountReservation', { count: countReservation })}
                                    stat={countReservation}
                                    locale={locale}
                                />
                            )}
                            {name === 'attendees' && countDeleted > 0 && (
                                <LabelStat label={t('ticket.attendees_stats.channels.labelCountDeleted', { count: countDeleted })} stat={countDeleted} locale={locale} />
                            )}
                            {name === 'revenue' && sumRevenueReservation > 0 && (
                                <LabelStat
                                    label={t('ticket.attendees_stats.channels.labelSumReservation')}
                                    stat={sumRevenueReservation}
                                    locale={locale}
                                    currency={event.currency || 'EUR'}
                                />
                            )}
                            {name === 'revenue' && sumRevenueDeleted > 0 && (
                                <LabelStat
                                    label={t('ticket.attendees_stats.channels.labelSumDeleted')}
                                    stat={sumRevenueDeleted}
                                    locale={locale}
                                    currency={event.currency || 'EUR'}
                                />
                            )}
                        </div>
                    }
                />
            )}
        </span>
    );
}

export default function CardsStats({ stats = null, countRates = 0, loadingRates = false }) {
    const locale = i18n.language;
    const [{ event }] = useAppContext();

    const statistics = React.useMemo(() => {
        if (!stats) {
            return null;
        }

        return stats.find(groupStats => groupStats.name === 'sales_by_channel_stats').value;
    }, [stats]);

    const countChannelsWithSales = React.useMemo(() => {
        if (!statistics) {
            return 0;
        }

        // We use the spread operator on the Statistics object to change the reference so as not
        // to alter the initial object when deleting the "total" and "distributors' keys in order
        // to isolate the main channels
        let _statisticsMainChannnels = { ...statistics };
        delete _statisticsMainChannnels['distributors'];
        delete _statisticsMainChannnels['total'];

        let countMainChannels = Object.values(_statisticsMainChannnels).filter(ch => ch.count_attendees_active > 0).length;
        let countDistributorsChannels = Object.values(statistics.distributors).filter(ch => ch.count_attendees_active > 0).length;

        return countMainChannels + countDistributorsChannels;
    }, [statistics]);

    const items = React.useMemo(
        () => [
            {
                name: 'channels',
                values: <Numbers locale={locale} value={countChannelsWithSales} />,
                loading: !statistics
            },
            {
                name: 'rates',
                values: countRates,
                loading: loadingRates
            },
            {
                name: 'attendees',
                values: <Numbers locale={locale} value={statistics?.total.count_attendees_active} />,
                loading: !statistics
            },
            {
                name: 'revenue',
                values: <Numbers locale={locale} value={statistics?.total.sum_revenue_active} currency={event.currency || 'EUR'} showNullDecimal />,
                loading: !statistics
            }
        ],
        [countChannelsWithSales, statistics, countRates, loadingRates, locale]
    );

    return (
        <Statistics.Group className={Style['stats_cards__group']}>
            {items.map(item => (
                <Statistics
                    className={Style['stats_card']}
                    key={item.name}
                    subtitle={
                        <SubtitleStatWithTooltip
                            name={item.name}
                            countDeleted={statistics?.total[`count_${item.name}_deleted`]}
                            countReservation={statistics?.total[`count_${item.name}_reservation`]}
                            sumRevenueDeleted={statistics?.total[`sum_${item.name}_deleted`]}
                            sumRevenueReservation={statistics?.total[`sum_${item.name}_reservation`]}
                        />
                    }
                    stat={item.loading ? <Loader active inline size="small" /> : item.values}
                />
            ))}
        </Statistics.Group>
    );
}

CardsStats.propTypes = {
    stats: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    countRates: PropTypes.number,
    loadingRates: PropTypes.bool
};
